// src/components/pages/CategoryCityPage.tsx

import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Building,
  Star,
  MapPin,
  Phone,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GLASS_CARD } from '@/components/ui/glass';
import Footer from '@/components/pages/shared/Footer';
import { useCategoryCity } from '@/components/hooks/useCategoryCity';

const PAGE_SIZE = 12;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const renderStars = (
  valoracion: number | null | undefined,
  total: number | null | undefined
) => {
  if (!valoracion || valoracion <= 0 || !total) return null;
  const rounded = Math.round(valoracion);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${
            i < rounded
              ? 'text-yellow-400 fill-yellow-400'
              : 'text-gray-200 fill-gray-200 dark:text-gray-700 dark:fill-gray-700'
          }`}
        />
      ))}
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 ml-0.5">
        {valoracion.toFixed(1)}
      </span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500">
        ({total})
      </span>
    </div>
  );
};

const CategoryCityPage = () => {
  const { categoria_slug = '', ciudad_slug = '' } = useParams<{
    categoria_slug: string;
    ciudad_slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Math.max(
    1,
    parseInt(searchParams.get('pagina') ?? '1', 10)
  );
  const [searchTerm, setSearchTerm] = useState('');

  const { empresas, loading, error, categoriaLabel, ciudadLabel, total } =
    useCategoryCity(categoria_slug, ciudad_slug);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (value.trim() && currentPage !== 1) {
        setSearchParams({});
      }
    },
    [currentPage, setSearchParams]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const filtered = searchTerm.trim()
    ? empresas.filter((e) =>
        e.empresa.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : empresas;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentData = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const canonicalBase = `https://bookoll.com/${categoria_slug}/${ciudad_slug}`;
  const canonicalUrl =
    currentPage === 1
      ? canonicalBase
      : `${canonicalBase}?pagina=${currentPage}`;
  const prevUrl =
    currentPage > 1
      ? currentPage === 2
        ? canonicalBase
        : `${canonicalBase}?pagina=${currentPage - 1}`
      : null;
  const nextUrl =
    currentPage < totalPages
      ? `${canonicalBase}?pagina=${currentPage + 1}`
      : null;

  const pageTitle =
    currentPage === 1
      ? `${categoriaLabel} en ${ciudadLabel} — Reserva cita online | Bookoll`
      : `${categoriaLabel} en ${ciudadLabel} — Página ${currentPage} | Bookoll`;

  const pageDescription =
    total > 0
      ? `Descubre ${total} ${categoriaLabel.toLowerCase()} en ${ciudadLabel}. Reserva cita online fácilmente con Bookoll.`
      : `Encuentra ${categoriaLabel.toLowerCase()} en ${ciudadLabel} y reserva cita online con Bookoll.`;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${canonicalUrl}#webpage`,
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    inLanguage: 'es-ES',
    isPartOf: {
      '@type': 'WebSite',
      '@id': 'https://bookoll.com/#website',
      name: 'Bookoll',
      url: 'https://bookoll.com',
    },
    breadcrumb: { '@id': `${canonicalBase}#breadcrumb` },
    ...(currentPage > 1 && {
      pagination: `Página ${currentPage} de ${totalPages}`,
    }),
  };

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${categoriaLabel} en ${ciudadLabel}`,
    numberOfItems: currentData.length,
    itemListElement: currentData.map((e, idx) => ({
      '@type': 'ListItem',
      position: (currentPage - 1) * PAGE_SIZE + idx + 1,
      url: `https://bookoll.com/${e.categoria_slug}/${e.ciudad_slug}/${e.empresa_slug}`,
      name: e.empresa,
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalBase}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://bookoll.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoriaLabel,
        item: `https://bookoll.com/${categoria_slug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: ciudadLabel,
        item: canonicalBase,
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`${categoriaLabel.toLowerCase()} ${ciudadLabel}, ${categoriaLabel.toLowerCase()} reserva online ${ciudadLabel}, cita ${categoriaLabel.toLowerCase()} ${ciudadLabel}, negocios ${ciudadLabel}, Bookoll`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        {prevUrl && <link rel="prev" href={prevUrl} />}
        {nextUrl && <link rel="next" href={nextUrl} />}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bookoll" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://bookoll.com/og-home.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={pageTitle} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bookoll" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://bookoll.com/og-home.jpg" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>

      {/* Header + Hero fusionados en un único gradiente continuo */}
      <div className="relative overflow-hidden text-white">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background:
              'linear-gradient(135deg, #103254 0%, #1a4a6e 50%, #288E7F 100%)',
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <header className="relative sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border border-white/20 hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            <Link to="/" className="shrink-0">
              <img
                src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
                alt="Bookoll"
                className="h-7"
              />
            </Link>
            <nav
              aria-label="Breadcrumb"
              className="hidden sm:flex items-center gap-1.5 text-sm text-white/60 ml-2"
            >
              <Link to="/" className="hover:text-white transition-colors">
                Inicio
              </Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white/80 font-medium capitalize">
                {categoriaLabel}
              </span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white font-semibold capitalize">
                {ciudadLabel}
              </span>
            </nav>
          </div>
        </header>

        <section className="relative py-16 px-4 sm:px-8">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 max-w-7xl mx-auto"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight capitalize">
              {categoriaLabel} <span className="text-white/70">en</span>{' '}
              {ciudadLabel}
            </h1>
            {total > 0 && (
              <p className="mt-3 text-white/70 text-base sm:text-lg">
                {total} negocio{total !== 1 ? 's' : ''} encontrado
                {total !== 1 ? 's' : ''}
              </p>
            )}
          </motion.div>
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
            <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-8">
              <path
                d="M0,20 C360,40 1080,0 1440,20 L1440,40 L0,40 Z"
                className="fill-white dark:fill-black"
              />
            </svg>
          </div>
        </section>
      </div>

      {/* Contenido */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-8 py-10">
        <div className="relative max-w-md mb-8">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
          <Input
            type="text"
            placeholder={`Buscar en ${ciudadLabel}...`}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 rounded-xl border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#103254]/20"
          />
        </div>

        {loading && (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#103254] dark:border-t-white dark:border-b-white/20" />
          </div>
        )}
        {error && (
          <div className="text-center text-red-500 dark:text-red-400 mt-10">
            {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className={`text-center py-20 rounded-2xl ${GLASS_CARD} text-gray-400 dark:text-gray-600`}>
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-200 dark:text-gray-700" />
            <p className="text-base font-medium text-gray-500 dark:text-gray-400">
              No encontramos {categoriaLabel.toLowerCase()} en {ciudadLabel}
            </p>
            <p className="text-sm mt-1">
              Prueba con otra búsqueda o{' '}
              <Link to="/explorar" className="text-[#103254] dark:text-[#4fb8a3] underline">
                explora todas las empresas
              </Link>
            </p>
          </div>
        )}

        {!loading && !error && currentData.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {currentData.map((empresa) => (
              <motion.article
                key={empresa.id_empresa}
                variants={itemVariants}
                className={`group rounded-2xl ${GLASS_CARD} hover:shadow-lg hover:border-[#103254]/20 dark:hover:border-white/20 transition-all duration-300 overflow-hidden cursor-pointer`}
                onClick={() =>
                  navigate(
                    `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`
                  )
                }
              >
                <div className="relative h-44 bg-gray-100 dark:bg-white/5 overflow-hidden">
                  {empresa.imagenes?.length ? (
                    <img
                      src={empresa.imagenes[0]}
                      alt={`${empresa.empresa} — ${categoriaLabel} en ${ciudadLabel}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : empresa.logo ? (
                    <img
                      src={empresa.logo}
                      alt={`Logo de ${empresa.empresa} — ${categoriaLabel} en ${ciudadLabel}`}
                      loading="lazy"
                      className="w-full h-full object-contain p-6"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h2 className="font-semibold text-[#0f172a] dark:text-white text-base leading-tight truncate mb-1">
                    {empresa.empresa}
                  </h2>
                  {renderStars(
                    empresa.valoracion_media_empresa,
                    empresa.total_valoraciones
                  )}
                  {empresa.descripcion && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                      {empresa.descripcion}
                    </p>
                  )}
                  <div className="flex flex-col gap-1 mt-3">
                    {empresa.telefono && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <Phone className="w-3 h-3 shrink-0" />
                        <span>{empresa.telefono}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="capitalize">{ciudadLabel}</span>
                    </div>
                  </div>

                  <div
                    className="flex gap-2 mt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {empresa.reservas_online_citas ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(`/client-booking/${empresa.id_empresa}`, {
                              state: { showBackButton: true },
                            })
                          }
                          className="flex-1 text-white text-xs font-semibold rounded-xl bg-[#103254] dark:bg-[#288E7F] hover:opacity-90"
                        >
                          <Calendar className="w-3.5 h-3.5 mr-1" />
                          Reservar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate(
                              `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`
                            )
                          }
                          className="flex-1 rounded-xl text-xs border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-[#103254]/40 hover:text-[#103254] dark:hover:border-white/20 dark:hover:text-white"
                        >
                          Ver más
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(
                            `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`
                          )
                        }
                        className="w-full rounded-xl text-xs border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-[#103254]/40 hover:text-[#103254] dark:hover:border-white/20 dark:hover:text-white"
                      >
                        Ver empresa
                      </Button>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-12">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() =>
                setSearchParams(
                  currentPage - 1 === 1
                    ? {}
                    : { pagina: String(currentPage - 1) }
                )
              }
              className="rounded-xl flex items-center gap-1.5 text-xs border-gray-200 dark:border-white/10 dark:text-gray-300"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Anterior
            </Button>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                const isActive = p === currentPage;
                const isNear =
                  p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1;
                if (!isNear) {
                  if (p === 2 || p === totalPages - 1)
                    return (
                      <span key={p} className="text-gray-400 dark:text-gray-500 text-xs px-1">
                        …
                      </span>
                    );
                  return null;
                }
                return (
                  <button
                    key={p}
                    onClick={() =>
                      setSearchParams(p === 1 ? {} : { pagina: String(p) })
                    }
                    className={`w-8 h-8 rounded-full text-xs font-semibold transition-all ${
                      isActive
                        ? 'bg-[#103254] dark:bg-[#288E7F] text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() =>
                setSearchParams({ pagina: String(currentPage + 1) })
              }
              className="rounded-xl flex items-center gap-1.5 text-xs border-gray-200 dark:border-white/10 dark:text-gray-300"
            >
              Siguiente <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CategoryCityPage;